
import React, { useState, useEffect, useMemo } from 'react';
import { User, Company, Lead, UserRole } from '../types';
import { api } from '../services/apiService';
import SelectField from '../components/SelectField';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';

interface AnalyticsViewProps {
  user: User;
  company: Company;
}

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#94a3b8'];

// Composant Tooltip personnalisé pour un style dark moderne
const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="orion-tooltip-custom">
      {label && (
        <div className="text-xs font-bold text-slate-300 mb-2 pb-2 border-b border-slate-700/50">
          {label}
        </div>
      )}
      {payload.map((entry: any, index: number) => (
        <div key={index} className="flex items-center justify-between gap-6 py-1">
          <span className="text-xs font-semibold text-slate-400 flex items-center gap-2">
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: entry.color || entry.fill }}
            />
            {entry.name}
          </span>
          <span className="text-sm font-black text-white">
            {entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const AnalyticsView: React.FC<AnalyticsViewProps> = ({ user, company }) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [agents, setAgents] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgentId, setSelectedAgentId] = useState<string>(user.role === UserRole.USER ? user.id : 'all');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const [allLeads, companyUsers] = await Promise.all([
        api.getLeads({ companyId: company.id, userId: user.id, role: user.role }),
        api.getCompanyUsers(company.id)
      ]);
      setLeads(allLeads);
      setAgents(companyUsers);
      setLoading(false);
    };
    fetchData();
  }, [company.id, user]);

  const stats = useMemo(() => {
    // Filtrage strict : si simple USER, on ne regarde que ses propres leads.
    // Sinon on regarde selon le filtre du sélecteur (qui est masqué pour l'USER)
    const filteredLeads = user.role === UserRole.USER 
      ? leads.filter(l => String(l.assignedTo) === String(user.id))
      : (selectedAgentId === 'all' ? leads : leads.filter(l => String(l.assignedTo) === String(selectedAgentId)));
    
    const total = filteredLeads.length;
    const won = filteredLeads.filter(l => l.status === 'won').length;
    const lost = filteredLeads.filter(l => l.status === 'lost').length;
    
    const activeClosed = won + lost;
    const transformationRate = activeClosed > 0 ? ((won / activeClosed) * 100).toFixed(1) : "0.0";
    
    const qualified = filteredLeads.filter(l => l.status === 'qualified' || l.status === 'won').length;
    const qualificationRate = total > 0 ? ((qualified / total) * 100).toFixed(1) : "0.0";

    const statusData = [
      { name: 'Nouveau', value: filteredLeads.filter(l => l.status === 'new').length },
      { name: 'Contacté', value: filteredLeads.filter(l => l.status === 'contacted').length },
      { name: 'Qualifié', value: filteredLeads.filter(l => l.status === 'qualified').length },
      { name: 'Gagné', value: won },
      { name: 'Perdu', value: lost },
    ].filter(s => s.value > 0);

    // Funnel data
    const newCount = filteredLeads.filter(l => l.status === 'new').length;
    const contactedCount = filteredLeads.filter(l => l.status === 'contacted').length;
    const qualifiedCount = filteredLeads.filter(l => l.status === 'qualified').length;

    const funnel = [
      { stage: 'Nouveau', count: newCount, color: '#6366f1' },
      { stage: 'Contacte', count: contactedCount, color: '#8b5cf6' },
      { stage: 'Qualifie', count: qualifiedCount, color: '#f59e0b' },
      { stage: 'Gagne', count: won, color: '#10b981' },
    ];
    // Conversion rates between stages
    const funnelRates = funnel.map((step, i) => {
      if (i === 0) return { ...step, rate: null };
      const prev = funnel[i - 1].count;
      return { ...step, rate: prev > 0 ? ((step.count / prev) * 100) : 0 };
    });

    // Pipeline value by status
    const pipelineValue = filteredLeads.reduce((sum, l) => sum + (parseFloat(l.potentialRevenue || '0') || 0), 0);
    const pipelineByStatus = [
      { name: 'Nouveau', value: filteredLeads.filter(l => l.status === 'new').reduce((s, l) => s + (parseFloat(l.potentialRevenue || '0') || 0), 0) },
      { name: 'Contacte', value: filteredLeads.filter(l => l.status === 'contacted').reduce((s, l) => s + (parseFloat(l.potentialRevenue || '0') || 0), 0) },
      { name: 'Qualifie', value: filteredLeads.filter(l => l.status === 'qualified').reduce((s, l) => s + (parseFloat(l.potentialRevenue || '0') || 0), 0) },
      { name: 'Gagne', value: filteredLeads.filter(l => l.status === 'won').reduce((s, l) => s + (parseFloat(l.potentialRevenue || '0') || 0), 0) },
    ];

    return { total, transformationRate, qualificationRate, statusData, won, lost, funnelRates, pipelineValue, pipelineByStatus };
  }, [leads, selectedAgentId, user]);

  // Calcul des statistiques par agent pour le tableau détaillé (Admins seulement)
  const agentsPerformance = useMemo(() => {
    if (user.role === UserRole.USER) return [];
    return agents.map(agent => {
      const agentLeads = leads.filter(l => String(l.assignedTo) === String(agent.id));
      const total = agentLeads.length;
      const won = agentLeads.filter(l => l.status === 'won').length;
      const lost = agentLeads.filter(l => l.status === 'lost').length;
      const qualified = agentLeads.filter(l => l.status === 'qualified' || l.status === 'won').length;
      
      const activeClosed = won + lost;
      const transfo = activeClosed > 0 ? (won / activeClosed) * 100 : 0;
      const qualif = total > 0 ? (qualified / total) * 100 : 0;

      return {
        id: agent.id,
        name: agent.name,
        email: agent.email,
        total,
        won,
        transfo,
        qualif
      };
    }).sort((a, b) => b.total - a.total);
  }, [leads, agents, user.role]);

  if (loading) return <div className="p-24 text-center animate-pulse text-slate-400">Analyse du pipeline...</div>;

  const isUser = user.role === UserRole.USER;

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-20">
      <div className="bg-white p-10 rounded-[2.5rem] border border-slate-100 shadow-sm flex flex-col md:flex-row justify-between items-center gap-6">
        <div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tight">Intelligence Pipeline</h2>
          <p className="text-slate-500 font-medium">
            {isUser ? 'Mes statistiques personnelles de performance.' : 'Analyse de performance globale et taux de succès.'}
          </p>
        </div>
        
        {/* Sélecteur affiché uniquement pour les admins */}
        {!isUser && (
          <div className="orion-analytics-selector-wrapper">
            <SelectField
              value={selectedAgentId}
              onChange={setSelectedAgentId}
              options={[
                { value: 'all', label: 'Vue Équipe Complète' },
                ...agents.map(a => ({ value: a.id, label: a.name }))
              ]}
              className="orion-analytics-selector"
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        {[
          { label: isUser ? 'Mes Leads' : 'Volume Leads', value: stats.total, color: 'text-indigo-600', sub: 'Base active' },
          { label: 'Taux Transformation', value: `${stats.transformationRate}%`, color: 'text-emerald-500', sub: 'Ratio Won/Lost' },
          { label: 'Qualification', value: `${stats.qualificationRate}%`, color: 'text-amber-500', sub: 'Leads qualifiés' },
          { label: 'Closing Net', value: stats.won, color: 'text-slate-900', sub: 'Contrats signes' },
          { label: 'Pipeline', value: `${stats.pipelineValue > 1000 ? `${(stats.pipelineValue / 1000).toFixed(0)}k` : stats.pipelineValue.toFixed(0)}€`, color: 'text-violet-600', sub: 'Valeur totale' }
        ].map((kpi, i) => (
          <div key={i} className="bg-white p-10 rounded-[2.5rem] border border-slate-100 shadow-sm hover:scale-[1.03] transition-all">
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">{kpi.label}</div>
            <div className={`text-4xl font-black ${kpi.color} mb-1 tracking-tighter`}>{kpi.value}</div>
            <div className="text-xs text-slate-400 font-medium">{kpi.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white p-10 rounded-[2.5rem] border border-slate-100 shadow-sm">
          <h3 className="text-xs font-bold text-slate-400 mb-10 uppercase tracking-widest">Répartition Opérationnelle</h3>
          <div className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={stats.statusData} cx="50%" cy="50%" innerRadius={80} outerRadius={120} paddingAngle={8} dataKey="value">
                  {stats.statusData.map((_, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="none" />)}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend iconType="circle" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        <div className="bg-white p-10 rounded-[2.5rem] border border-slate-100 shadow-sm">
          <h3 className="text-xs font-bold text-slate-400 mb-10 uppercase tracking-widest">Flux d'Activité Pipeline</h3>
          <div className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.statusData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 'bold' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip
                  cursor={{ fill: 'rgba(99, 102, 241, 0.05)' }}
                  content={<CustomTooltip />}
                />
                <Bar dataKey="value" fill="#6366f1" radius={[15, 15, 0, 0]} barSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Funnel de Conversion */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white p-10 rounded-[2.5rem] border border-slate-100 shadow-sm">
          <h3 className="text-xs font-bold text-slate-400 mb-8 uppercase tracking-widest">Funnel de Conversion</h3>
          <div className="space-y-4">
            {stats.funnelRates.map((step, i) => {
              const maxCount = Math.max(...stats.funnelRates.map(s => s.count), 1);
              const widthPct = Math.max((step.count / maxCount) * 100, 8);
              return (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-bold text-slate-600">{step.stage}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-black text-slate-800">{step.count}</span>
                      {step.rate !== null && (
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${step.rate >= 50 ? 'bg-emerald-100 text-emerald-700' : step.rate >= 25 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>
                          {step.rate.toFixed(0)}%
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="w-full h-8 bg-slate-50 rounded-xl overflow-hidden">
                    <div
                      className="h-full rounded-xl transition-all duration-700"
                      style={{ width: `${widthPct}%`, backgroundColor: step.color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white p-10 rounded-[2.5rem] border border-slate-100 shadow-sm">
          <h3 className="text-xs font-bold text-slate-400 mb-8 uppercase tracking-widest">Valeur Pipeline par Statut</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.pipelineByStatus} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k€` : `${v}€`} />
                <YAxis type="category" dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11, fontWeight: 'bold' }} axisLine={false} tickLine={false} width={80} />
                <Tooltip content={<CustomTooltip />} formatter={(value: number) => [`${value.toLocaleString()}€`, 'Montant']} />
                <Bar dataKey="value" radius={[0, 12, 12, 0]} barSize={28}>
                  {stats.pipelineByStatus.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={['#6366f1', '#8b5cf6', '#f59e0b', '#10b981'][index]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Tableau de Performance - Uniquement pour les admins */}
      {!isUser && (
        <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-10 border-b border-slate-700/30 flex items-center justify-between">
            <div>
                <h3 className="text-xl font-black text-slate-100 tracking-tight">Performance par Agent</h3>
                <p className="text-sm text-slate-400 font-medium">Classement basé sur le volume et le closing.</p>
            </div>
            <div className="orion-analytics-badge">
                <span className="orion-analytics-badge-dot"></span>
                Live Feed
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 text-slate-400 text-[10px] font-black uppercase tracking-widest border-b border-slate-100">
                <tr>
                  <th className="px-10 py-5">Agent</th>
                  <th className="px-10 py-5">Volume</th>
                  <th className="px-10 py-5">Qualification</th>
                  <th className="px-10 py-5">Transformation</th>
                  <th className="px-10 py-5 text-right">Closing Net</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {agentsPerformance.map((perf) => (
                  <tr key={perf.id} className="hover:bg-slate-50/50 transition-colors group">
                    <td className="px-10 py-6">
                      <div className="flex items-center gap-4">
                        <img 
                          src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${perf.email}`} 
                          className="w-10 h-10 rounded-xl bg-slate-100 border border-slate-200" 
                          alt="Avatar" 
                        />
                        <div>
                          <div className="font-black text-slate-800 group-hover:text-indigo-600 transition-colors">{perf.name}</div>
                          <div className="text-[10px] text-slate-400 font-medium">{perf.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-10 py-6">
                      <div className="text-sm font-black text-slate-700">{perf.total}</div>
                      <div className="text-[10px] text-slate-400 uppercase font-bold tracking-tighter">Leads affectés</div>
                    </td>
                    <td className="px-10 py-6">
                      <div className="flex flex-col gap-1">
                        <div className="text-sm font-black text-slate-700">{perf.qualif.toFixed(1)}%</div>
                        <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-amber-400 rounded-full" style={{ width: `${perf.qualif}%` }}></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-10 py-6">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <div className={`px-2 py-0.5 rounded-lg text-[10px] font-black ${perf.transfo > 20 ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                            {perf.transfo.toFixed(1)}%
                          </div>
                        </div>
                        <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${perf.transfo > 20 ? 'bg-emerald-500' : 'bg-rose-400'}`} style={{ width: `${perf.transfo}%` }}></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-10 py-6 text-right">
                      <div className="text-lg font-black text-indigo-600 tracking-tighter">{perf.won}</div>
                      <div className="text-[10px] text-slate-400 uppercase font-bold tracking-widest">Signatures</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsView;
